const PARIS_TIMEZONE = "Europe/Paris";

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  catch {
    return PARIS_TIMEZONE;
  }
};

export const getTimezoneCityLabel = (timezone: string): string => {
  const segments = timezone.split("/");
  const city = segments[segments.length - 1] ?? timezone;
  return city.replace(/_/g, " ");
};

export type FormatMatchDateOptions = {
  timezone?: string
  locale?: string
};

export const formatMatchDateTime = (
  iso: string,
  { timezone = getBrowserTimezone(), locale = "fr-FR" }: FormatMatchDateOptions = {},
): string => {
  const date = new Date(iso);
  const dayPart = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: timezone,
  }).format(date);
  const timePart = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  }).format(date);
  return `${dayPart} à ${timePart}`;
};

export const formatMatchDateOnly = (
  iso: string,
  { timezone = getBrowserTimezone(), locale = "fr-FR" }: FormatMatchDateOptions = {},
): string => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: timezone,
  }).format(date);
};

export const toDateKey = (
  iso: string,
  { timezone = getBrowserTimezone() }: { timezone?: string } = {},
): string => {
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: timezone,
  }).formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  return `${year}-${month}-${day}`;
};

export const formatChipDateLabel = (
  iso: string,
  { timezone = getBrowserTimezone(), locale = "fr-FR" }: FormatMatchDateOptions = {},
): string => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: timezone,
  }).format(date).replace(/\.$/, "");
};

export const formatMatchTimeOnly = (
  iso: string,
  { timezone = getBrowserTimezone(), locale = "fr-FR" }: FormatMatchDateOptions = {},
): string => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  }).format(date);
};

export type CountdownParts = {
  totalSeconds: number
  days: number
  hours: number
  minutes: number
  seconds: number
  hasReachedZero: boolean
};

export const getCountdownParts = (
  targetIso: string,
  nowMs: number = Date.now(),
): CountdownParts => {
  const targetMs = new Date(targetIso).getTime();
  const diffMs = targetMs - nowMs;
  const totalSeconds = Math.floor(diffMs / 1000);
  if (diffMs <= 0) {
    return {
      totalSeconds, days: 0, hours: 0, minutes: 0, seconds: 0, hasReachedZero: true,
    };
  }
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    totalSeconds, days, hours, minutes, seconds, hasReachedZero: false,
  };
};

const pad = (n: number) => n.toString().padStart(2, "0");

const IMMINENT_THRESHOLD_SECONDS = 5 * 60;

export const formatCountdown = (parts: CountdownParts): string => {
  if (parts.hasReachedZero) {
    const elapsedSeconds = Math.abs(parts.totalSeconds);
    return elapsedSeconds < IMMINENT_THRESHOLD_SECONDS ? "Match imminent" : "Match débuté";
  }
  if (parts.days > 0) {
    return `${parts.days}j ${pad(parts.hours)}h ${pad(parts.minutes)}m`;
  }
  return `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
};

export const formatDateRange = (
  startsIso: string,
  endsIso: string,
  { timezone = getBrowserTimezone(), locale = "fr-FR" }: FormatMatchDateOptions = {},
): string => {
  const starts = new Date(startsIso);
  const ends = new Date(endsIso);

  const yearFmt = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone: timezone });
  const sameYear = yearFmt.format(starts) === yearFmt.format(ends);

  const startFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: timezone,
  });
  const endFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: timezone,
  });

  return `Du ${startFmt.format(starts)} au ${endFmt.format(ends)}`;
};
